<template>
  <!--
    CartWidget — centralized "Your Cart" staging tray.

    Renders the same visual tray currently shown on Account/Index.vue
    in its `.staging-tray-wrapper` block, but lives in one place so
    look-and-feel changes are made once and apply everywhere.

    Two modes, picked by the `standalone` prop:

      • Controlled mode (default): host page owns `selectedParts` /
        `selectedPartsQuote`, passes them in as props, and handles
        mutation through events. Used by pages that have their own
        cart-add picker modal (Search.vue, Orders/Create.vue,
        RFQ/Create.vue, and Account/Index.vue).

      • Standalone mode (set `standalone` to true): widget owns its
        own state, fetches the active order + quote carts itself via
        apiService.getCart(), and listens on Vue.prototype.$cartBus
        for 'cart-updated' events so it stays in sync when other
        components mutate the cart. Used by pages that just need
        cart visibility (OrderHistory, SavedCarts, Documents).

    Collapse state is shared across the whole app via
    localStorage.cartCollapsed so toggling it on one page persists
    when the user navigates to another.

    The collapsed pill (the small floating cart-with-counts on the
    mid-right edge) is rendered through <portal to="cart-pill">. The
    matching <portal-target name="cart-pill"> sits at the root of
    App.vue, ensuring the pill escapes whatever stacking context the
    host page lives in (same fix pattern as the bottom-right utility
    toggles in Head.vue).
  -->
  <div class="cart-widget-host"
       :class="{
         'cart-widget-floating': floating,
         'cart-host-collapsed': floating && (collapsed || !hasItems)
       }"
       v-if="visible">

    <!-- ============================================================
         EXPANDED TRAY
         ============================================================
         Rendered inline in the host page's grid cell when not
         collapsed AND the cart has items. Same DOM shape as the old
         .staging-tray-wrapper block in Account/Index.vue so the
         existing CSS class names continue to match.

         hasItems v-if: when both queues are empty, render nothing
         inline. This matches the original behaviour on Account
         (the col-lg-4 was wrapped in v-if="hasStagingItems"). Host
         pages listen for the inline-visibility-change event to
         collapse their hosting column to nothing when the inline
         tray disappears. -->
    <div class="staging-tray-wrapper" v-if="!collapsed && hasItems">
      <div class="staging-tray-header">
        <span class="staging-tray-icon" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg"
               viewBox="0 0 576 512"
               width="14" height="14">
            <path fill="currentColor"
              d="M0 24C0 10.7 10.7 0 24 0H69.5c22 0 41.5 12.8 50.6 32h411c26.3 0 45.5 25 38.6 50.4l-41 152.3c-8.5 31.4-37 53.3-69.5 53.3H170.7l5.4 28.5c2.2 11.3 12.1 19.5 23.6 19.5H488c13.3 0 24 10.7 24 24s-10.7 24-24 24H199.7c-34.6 0-64.3-24.6-70.7-58.5L77.4 54.5c-.7-3.8-4-6.5-7.9-6.5H24C10.7 48 0 37.3 0 24zM128 464a48 48 0 1 1 96 0 48 48 0 1 1 -96 0zm336-48a48 48 0 1 1 0 96 48 48 0 1 1 0-96z"/>
          </svg>
        </span>
        <span class="staging-tray-title">Your Cart</span>

        <!-- Collapse button — sits on the right side of the header
             row. Mirrors the look of the .util-close × on the
             utility toggles, but with a different glyph (chevron
             pointing right toward the rail where the pill will
             appear) and an accessible label. -->
        <button type="button"
                class="cart-collapse-btn"
                @click="collapse"
                aria-label="Collapse cart to side panel"
                title="Collapse cart to side">
          <svg xmlns="http://www.w3.org/2000/svg"
               viewBox="0 0 24 24"
               width="14" height="14"
               fill="none"
               stroke="currentColor"
               stroke-width="2.5"
               stroke-linecap="round"
               stroke-linejoin="round"
               aria-hidden="true">
            <polyline points="9 6 15 12 9 18"/>
          </svg>
        </button>
      </div>

      <div class="insights-selected-tray staging-tray-body">

        <!-- ===== ORDERS SECTION ===== -->
        <template v-if="groupedSelections.length > 0">
          <div class="insights-selected-title">
            <span class="insights-selected-title-text">Your Order In Progress</span>
          </div>
          <div class="insights-selected-header">
            <span class="insights-selected-label">
              {{ effectiveParts.length }} {{ effectiveParts.length === 1 ? 'part' : 'parts' }} across
              {{ groupedSelections.length }}
              {{ groupedSelections.length === 1 ? 'order' : 'orders' }}
            </span>
            <button type="button"
                    class="insights-selected-clear"
                    @click="onClear('order')"
                    title="Clear orders">
              Clear all
            </button>
          </div>

          <div class="insights-order-group"
               v-for="g in groupedSelections"
               :key="'cw-grp-' + g.poNumber">
            <div class="insights-order-group-head">
              <span class="insights-order-group-num">Purchase Order #{{ g.poNumber }}</span>
              <span class="insights-order-group-meta">
                {{ g.parts.length }} {{ g.parts.length === 1 ? 'part' : 'parts' }}
              </span>
            </div>
            <ul class="insights-queue-rows">
              <!-- Single row: code · 2% chip · price · qty · remove. Code
                   ellipsis-truncates so everything fits one line in the
                   narrow widget; qty group is pushed right via margin-left. -->
              <li class="insights-queue-row"
                  v-for="p in g.parts"
                  :key="'cw-qrow-' + p.partId + '-' + g.poNumber">
                <span class="insights-queue-row-code">{{ p.partCode }}</span>
                <span v-if="isWearEligible(p)"
                      class="insights-queue-discount"
                      title="Qualifies for a 2% wear-part discount">2% Discount</span>
                <span v-if="p.price" class="insights-queue-row-price">{{ fmtPrice(p.price) }}</span>
                <div class="insights-queue-row-qty">
                  <input type="number"
                         min="1"
                         step="1"
                         :value="getQtyDraft(p, 'order')"
                         @input="setQtyDraft(p, 'order', $event.target.value)"
                         @keyup.enter="commitQty(p, 'order')"
                         class="insights-qty-input"
                         :aria-label="'Quantity for ' + p.partCode" />
                  <!-- Green check saves the edited quantity. Editing the
                       field updates a draft only; clicking the check (or
                       pressing Enter) commits it to the cart. The check
                       only appears once the typed value differs from the
                       saved quantity (qtyChanged), so an untouched row
                       shows no affordance. -->
                  <button type="button"
                          v-if="qtyChanged(p, 'order')"
                          class="insights-qty-save"
                          @click="commitQty(p, 'order')"
                          :title="'Save quantity for ' + p.partCode"
                          aria-label="Save quantity">
                    <svg xmlns="http://www.w3.org/2000/svg"
                         viewBox="0 0 24 24" width="13" height="13"
                         fill="none" stroke="currentColor" stroke-width="3"
                         stroke-linecap="round" stroke-linejoin="round"
                         aria-hidden="true">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </button>
                  <span class="insights-qty-label">qty</span>
                </div>
                <button type="button"
                        class="insights-queue-row-remove"
                        @click="onRemove(p, 'order')"
                        :title="'Remove ' + p.partCode + ' from Purchase Order #' + g.poNumber">
                  ×
                </button>
              </li>
            </ul>
          </div>
          <!-- Continue button — uses ORDER-count (distinct PO count
               via orderCount computed), not parts.length. The
               orderCount computed matches Head.vue's loadCartCount
               algorithm exactly (distinct non-blank poNumber values)
               so this button's number agrees with the side pill
               badge and with what the user reads as "how many
               orders" elsewhere in the app. CSS upper-cases the
               text on screen via text-transform: uppercase on
               .staging-continue-btn; the JS literal uses
               capital-first 'Order'/'Orders' so source reads
               naturally. -->
          <button type="button"
                  class="staging-continue-btn"
                  @click="onContinueOrder">
            Review and Place {{ orderCount }}
            {{ orderCount === 1 ? 'Order' : 'Orders' }}<template v-if="orderTotal > 0"> for {{ fmtPrice(orderTotal) }}</template> →
          </button>
        </template>

        <!-- ===== QUOTES SECTION ===== -->
        <template v-if="groupedQuoteSelections.length > 0">
          <div class="insights-selected-title insights-selected-title-quote">
            <span class="insights-selected-title-text">Your Quotes In Progress</span>
          </div>
          <div class="insights-selected-header">
            <span class="insights-selected-label">
              {{ effectiveQuoteParts.length }} {{ effectiveQuoteParts.length === 1 ? 'part' : 'parts' }} across
              {{ groupedQuoteSelections.length }}
              {{ groupedQuoteSelections.length === 1 ? 'quote' : 'quotes' }}
            </span>
            <button type="button"
                    class="insights-selected-clear"
                    @click="onClear('quote')"
                    title="Clear quotes">
              Clear all
            </button>
          </div>

          <div class="insights-order-group insights-quote-group"
               v-for="g in groupedQuoteSelections"
               :key="'cw-qgrp-' + g.poNumber">
            <div class="insights-order-group-head">
              <!-- Label per user spec: quote section uses
                   "Quote Reference #" rather than the leftover
                   "Reference #" or the order-section "Purchase
                   Order #". -->
              <span class="insights-order-group-num">Quote Reference #{{ g.poNumber }}</span>
              <span class="insights-order-group-meta">
                {{ g.parts.length }} {{ g.parts.length === 1 ? 'part' : 'parts' }}
              </span>
            </div>
            <ul class="insights-queue-rows">
              <li class="insights-queue-row"
                  v-for="p in g.parts"
                  :key="'cw-qqrow-' + p.partId + '-' + g.poNumber">
                <span class="insights-queue-row-code">{{ p.partCode }}</span>
                <span v-if="isWearEligible(p)"
                      class="insights-queue-discount"
                      title="Qualifies for a 2% wear-part discount">2% Discount</span>
                <span v-if="p.price" class="insights-queue-row-price">{{ fmtPrice(p.price) }}</span>
                <div class="insights-queue-row-qty">
                  <input type="number"
                         min="1"
                         step="1"
                         :value="getQtyDraft(p, 'quote')"
                         @input="setQtyDraft(p, 'quote', $event.target.value)"
                         @keyup.enter="commitQty(p, 'quote')"
                         class="insights-qty-input"
                         :aria-label="'Quantity for ' + p.partCode" />
                  <button type="button"
                          v-if="qtyChanged(p, 'quote')"
                          class="insights-qty-save"
                          @click="commitQty(p, 'quote')"
                          :title="'Save quantity for ' + p.partCode"
                          aria-label="Save quantity">
                    <svg xmlns="http://www.w3.org/2000/svg"
                         viewBox="0 0 24 24" width="13" height="13"
                         fill="none" stroke="currentColor" stroke-width="3"
                         stroke-linecap="round" stroke-linejoin="round"
                         aria-hidden="true">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </button>
                  <span class="insights-qty-label">qty</span>
                </div>
                <button type="button"
                        class="insights-queue-row-remove"
                        @click="onRemove(p, 'quote')"
                        :title="'Remove ' + p.partCode + ' from Quote Reference #' + g.poNumber">
                  ×
                </button>
              </li>
            </ul>
          </div>
          <!-- Continue button — uses QUOTE-count (distinct quote
               reference count via quoteCount computed), same
               algorithm as orderCount above. See comment on the
               orders button for full rationale. -->
          <button type="button"
                  class="staging-continue-btn staging-continue-btn-quote"
                  @click="onContinueQuote">
            Review and Save {{ quoteCount }}
            {{ quoteCount === 1 ? 'Quote' : 'Quotes' }}<template v-if="quoteTotal > 0"> for {{ fmtPrice(quoteTotal) }}</template> →
          </button>
        </template>
      </div>
    </div>

    <!-- ============================================================
         COLLAPSED PILL (portal'd to App.vue root)
         ============================================================
         Mounted into <portal-target name="cart-pill"> at the root of
         App.vue so it escapes whatever stacking context this widget's
         host happens to live in. Position: fixed, mid-right edge.
         Click anywhere on the pill to expand the tray again. -->
    <portal to="cart-pill">
      <div class="cart-pill"
           v-if="collapsed"
           @click="expand"
           role="button"
           tabindex="0"
           @keydown.enter.space.prevent="expand"
           aria-label="Open cart">
        <div class="cart-pill-icon">
          <svg xmlns="http://www.w3.org/2000/svg"
               viewBox="0 0 576 512"
               width="22" height="22"
               aria-hidden="true">
            <path fill="currentColor"
              d="M0 24C0 10.7 10.7 0 24 0H69.5c22 0 41.5 12.8 50.6 32h411c26.3 0 45.5 25 38.6 50.4l-41 152.3c-8.5 31.4-37 53.3-69.5 53.3H170.7l5.4 28.5c2.2 11.3 12.1 19.5 23.6 19.5H488c13.3 0 24 10.7 24 24s-10.7 24-24 24H199.7c-34.6 0-64.3-24.6-70.7-58.5L77.4 54.5c-.7-3.8-4-6.5-7.9-6.5H24C10.7 48 0 37.3 0 24zM128 464a48 48 0 1 1 96 0 48 48 0 1 1 -96 0zm336-48a48 48 0 1 1 0 96 48 48 0 1 1 0-96z"/>
          </svg>
        </div>
        <!-- Counts on the pill mirror the (now-removed) header
             "View Cart: Orders | Quotes" badges: each shows the
             number of distinct purchase orders / quote references
             on its respective cart, NOT the total parts count.
             That's what the user actually thinks of as "how many
             orders / quotes do I have" — one PO with three parts
             counts as one order, matching the "Order #1" /
             "Quote Reference #ABC" cards rendered in the tray.

             Algorithm is identical to Head.vue's loadCartCount:
             distinct non-blank poNumber values. Implemented in
             the orderCount / quoteCount computeds below. -->
        <div class="cart-pill-counts" v-if="orderCount || quoteCount">
          <span class="cart-pill-badge cart-pill-badge-orders"
                v-if="orderCount"
                :title="orderCount + ' open order' + (orderCount === 1 ? '' : 's')">
            {{ orderCount }}
          </span>
          <span class="cart-pill-badge cart-pill-badge-quotes"
                v-if="quoteCount"
                :title="quoteCount + ' open quote' + (quoteCount === 1 ? '' : 's')">
            {{ quoteCount }}
          </span>
        </div>
      </div>
    </portal>
  </div>
</template>

<script>
import Vue from "vue";
import FeApiService from "@/assets/js/FeApiService";

const apiService = new FeApiService();

// Key under which the collapsed/expanded state persists. Read at
// mount, written whenever the user toggles it. Same key shape as
// other per-feature flags (jcsteele.<feature>).
const COLLAPSE_STORAGE_KEY = "jcsteele.cart.collapsed";

export default {
  name: "CartWidget",

  props: {
    // Controlled-mode inputs. Host page owns these arrays and the
    // widget renders them. When the user removes / clears / changes
    // qty, the widget emits an event and the host updates its own
    // arrays + runs its own scheduleSyncToCart*.
    parts: {
      type: Array,
      default: null,
    },
    quoteParts: {
      type: Array,
      default: null,
    },

    // Set true on pages that don't otherwise manage cart state
    // (OrderHistory, SavedCarts, Documents). Widget self-fetches
    // and self-manages.
    standalone: {
      type: Boolean,
      default: false,
    },

    // Optional: hide entirely (the host page can opt out — e.g.
    // the cart page itself shouldn't show its own widget). Defaults
    // to true so the widget is visible wherever it's mounted.
    visible: {
      type: Boolean,
      default: true,
    },

    // Floating mode. Set true when the widget is mounted in the
    // global right-edge stack in App.vue (rather than inside a host
    // page's grid column). The collapsed pill already floats via the
    // cart-pill portal regardless; this flag only adjusts the
    // EXPANDED tray so it sizes to the fixed-width stack and scrolls
    // internally when the cart is long — keeping the Top 5 panel
    // stacked below it reachable. Default false preserves the
    // original inline-in-column rendering for any controlled host.
    floating: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      // Used only in standalone mode. Mirrors the host pages'
      // selectedParts / selectedPartsQuote shapes exactly so the
      // template doesn't have to branch on mode.
      internalParts: [],
      internalQuoteParts: [],

      // True when the user has collapsed the tray to the side
      // pill. Initialised from localStorage in created() so the
      // first render is correct (no flash of expanded → collapsed
      // on every page load).
      collapsed: false,

      // Standalone mode only: handle to the cart-bus listener so we
      // can $off it in beforeDestroy.
      _cartBusHandler: null,

      // Per-row quantity drafts, keyed by "<dest>|<partId>|<poNumber>".
      // Editing a row's qty input updates its draft only; the green
      // check (or Enter) commits it to the cart. Keeps qty edits from
      // saving on every keystroke and gives the user an explicit save.
      qtyDrafts: {},

      // Company-scoped wear-eligible catalog part-id map (same source the
      // /cart page uses). Drives the "2% Discount" chip on order rows.
      // Reloaded when the selected company changes.
      wearPartIds: {},
    };
  },

  computed: {
    // Source-of-truth arrays the rest of the template reads from.
    // In controlled mode these are the props; in standalone mode
    // they're the internal arrays. Centralised here so the rest of
    // the computeds + template stay mode-agnostic.
    effectiveParts() {
      if (this.standalone) return this.internalParts || [];
      return this.parts || [];
    },
    effectiveQuoteParts() {
      if (this.standalone) return this.internalQuoteParts || [];
      return this.quoteParts || [];
    },

    // Same grouping logic as Account/Index.vue and Search/Index.vue.
    // Keeping the shape identical means the template is byte-similar
    // to the original.
    groupedSelections() {
      var groups = {};
      var poOrder = [];
      (this.effectiveParts || []).forEach(function (p) {
        var po = p.poNumber || "(no PO)";
        if (!groups[po]) {
          groups[po] = { poNumber: po, parts: [] };
          poOrder.push(po);
        }
        groups[po].parts.push(p);
      });
      return poOrder.map(function (po, i) {
        return {
          orderNumber: i + 1,
          poNumber: groups[po].poNumber,
          parts: groups[po].parts,
        };
      });
    },
    groupedQuoteSelections() {
      var groups = {};
      var order = [];
      (this.effectiveQuoteParts || []).forEach(function (p) {
        var po = p.poNumber || "(no PO)";
        if (!groups[po]) {
          groups[po] = { poNumber: po, parts: [] };
          order.push(po);
        }
        groups[po].parts.push(p);
      });
      return order.map(function (po, i) {
        return {
          orderNumber: i + 1,
          poNumber: groups[po].poNumber,
          parts: groups[po].parts,
        };
      });
    },

    // Drives the v-if on the entire widget. Hidden when both queues
    // are empty in either mode, mirroring the original hasStagingItems
    // behavior on Account.
    hasItems() {
      return this.effectiveParts.length > 0 || this.effectiveQuoteParts.length > 0;
    },

    // ===== PILL BADGE COUNTS =====
    // Number of distinct purchase orders (or quote references) on
    // each cart side. Used by:
    //   - The collapsed side pill (orange/dark badges)
    //   - The CONTINUE WITH ... ORDER(S)/QUOTE(S) button text
    // so the displayed counts agree with what the user reads as
    // "Orders" and "Quotes" on /cart and on the staging tray's PO
    // group cards — NOT a flat parts total.
    //
    // Algorithm matches Head.vue's loadCartCount exactly:
    //   - Walk every queued part.
    //   - Track unique poNumber values, ignoring blank/missing ones.
    //   - The result is "1" for a cart with one PO carrying many
    //     parts, "2" if the user has two POs going at once, etc.
    //
    // Keeping this algorithm in lockstep with Head.vue's matters
    // because the cart-expiry timer logic in Head.vue still uses
    // its own copy of these counts internally — having the pill
    // show a different number than the header used to show would
    // be a usability regression even though the header badges
    // themselves are gone now.
    orderCount() {
      return this._distinctPoCount(this.effectiveParts);
    },
    quoteCount() {
      return this._distinctPoCount(this.effectiveQuoteParts);
    },

    // Gross order subtotal (sum of price × qty across all order lines).
    // Mirrors the /cart page's per-line price×qty math; the 2% wear
    // discount is shown as a chip only, NOT netted out of the total
    // (same as /cart, where the discount is applied at invoicing).
    orderTotal() {
      return (this.effectiveParts || []).reduce(function (sum, p) {
        var price = Number(p && p.price) || 0;
        var qty = Number(p && p.quantity) || 1;
        return sum + price * qty;
      }, 0);
    },
    // Gross quote subtotal — same math as orderTotal, for the quote button's
    // "for $X" (parity with the order button).
    quoteTotal() {
      return (this.effectiveQuoteParts || []).reduce(function (sum, p) {
        var price = Number(p && p.price) || 0;
        var qty = Number(p && p.quantity) || 1;
        return sum + price * qty;
      }, 0);
    },

    // True when the widget's INLINE tray is currently rendered (as
    // opposed to the portal'd side pill). Host pages listen for this
    // via the inline-visibility-change event so they can collapse
    // their hosting column to nothing when the inline tray is gone
    // — e.g. OrderHistory's right-rail col-lg-3 disappears so the
    // table can stretch back to col-12.
    inlineVisible() {
      return this.visible && !this.collapsed && this.hasItems;
    },
    // Primitive key so a watcher fires whenever either count changes —
    // lets a host (e.g. the mobile cart bar in App.vue) show order/quote
    // badges without re-deriving the cart itself.
    cartCountsKey() {
      return this.orderCount + "/" + this.quoteCount;
    },
  },

  watch: {
    // Emit on every change so host pages can react without polling.
    // Immediate: true so the first render also tells the host
    // what's up — covers the case where the widget mounts already-
    // collapsed and the host needs to know that up-front.
    inlineVisible: {
      handler: function (v) {
        this.$emit("inline-visibility-change", v);
      },
      immediate: true,
    },
    cartCountsKey: {
      handler: function () {
        this.$emit("counts-change", {
          orders: this.orderCount,
          quotes: this.quoteCount,
        });
      },
      immediate: true,
    },
  },

  created() {
    // Hydrate collapse state before first render to avoid a flash
    // of one state followed by the other. Defensively wrapped — a
    // throwing localStorage (private-mode iOS, disabled cookies)
    // just defaults to expanded.
    try {
      var raw = localStorage.getItem(COLLAPSE_STORAGE_KEY);
      this.collapsed = raw === "1" || raw === "true";
    } catch (e) { /* no-op */ }
  },

  mounted() {
    // Load the company's wear-eligible part ids for the "2% Discount" chip
    // (used in both standalone and controlled modes).
    this.loadWearPartIds();
    if (this.standalone) {
      // Initial population from the active carts. Same two-call
      // pattern the host pages use on mount.
      this.syncStandaloneFromActiveCart();
      this.syncStandaloneFromActiveQuoteCart();

      // Listen for cart-updated emissions from other components
      // (Order/Create and RFQ/Create both $emit this after
      // mutating the server cart). The Vue.prototype.$cartBus is
      // lazy-initialised in Head.vue's mounted hook; guard for the
      // (unlikely) case where Head hasn't mounted yet.
      if (!Vue.prototype.$cartBus) {
        Vue.prototype.$cartBus = new Vue();
      }
      this._cartBusHandler = () => {
        this.syncStandaloneFromActiveCart();
        this.syncStandaloneFromActiveQuoteCart();
      };
      Vue.prototype.$cartBus.$on("cart-updated", this._cartBusHandler);
    }
  },

  beforeDestroy() {
    if (this.standalone && Vue.prototype.$cartBus && this._cartBusHandler) {
      Vue.prototype.$cartBus.$off("cart-updated", this._cartBusHandler);
    }
  },

  methods: {
    // ===== INTERNAL HELPERS =====
    // Count distinct non-blank poNumber values across an array
    // of part rows. Used by the orderCount / quoteCount computeds
    // to drive the pill badge numbers AND the continue-button
    // labels. Algorithm is byte-equivalent to Head.vue's
    // loadCartCount inner loop — both walk the same shape of
    // data and the same blank-PO skip rule applies. If either
    // implementation changes, update the other to match so the
    // pill, the button text, and the cart-expiry timer's
    // internal counts stay in lockstep.
    _distinctPoCount(arr) {
      if (!arr || arr.length === 0) return 0;
      var seen = {};
      var count = 0;
      for (var i = 0; i < arr.length; i++) {
        var p = arr[i];
        var po = p && p.poNumber != null
          ? String(p.poNumber).trim()
          : "";
        if (!po) continue;
        if (!seen[po]) {
          seen[po] = true;
          count++;
        }
      }
      return count;
    },

    // ===== COLLAPSE / EXPAND =====
    collapse() {
      this.collapsed = true;
      this._persistCollapsed(true);
    },
    expand() {
      this.collapsed = false;
      this._persistCollapsed(false);
    },
    _persistCollapsed(v) {
      try {
        localStorage.setItem(COLLAPSE_STORAGE_KEY, v ? "1" : "0");
      } catch (e) { /* no-op */ }
    },

    // ===== EVENT HANDLERS =====
    // In controlled mode every interaction becomes an $emit and the
    // parent updates its own state. In standalone mode we mutate
    // our internal arrays AND emit the same events (in case
    // anything else cares), plus tell the server.
    onRemove(part, destination) {
      if (this.standalone) {
        this._removeStandalone(part, destination);
      } else {
        this.$emit("remove", { part: part, destination: destination });
      }
    },
    onClear(destination) {
      if (this.standalone) {
        this._clearStandalone(destination);
      } else {
        this.$emit("clear", { destination: destination });
      }
    },
    // ===== QUANTITY EDIT (draft → explicit green-check commit) =====
    // Editing a row's qty input updates a per-row DRAFT only; the green
    // check (or Enter) commits it. This replaces the old save-on-every-
    // keystroke behavior with a deliberate save the user controls.
    _qtyKey(part, dest) {
      return (
        (dest === "quote" ? "quote" : "order") +
        "|" + part.partId +
        "|" + (part.poNumber || "")
      );
    },
    getQtyDraft(part, dest) {
      var k = this._qtyKey(part, dest);
      return Object.prototype.hasOwnProperty.call(this.qtyDrafts, k)
        ? this.qtyDrafts[k]
        : part.quantity;
    },
    setQtyDraft(part, dest, value) {
      this.$set(this.qtyDrafts, this._qtyKey(part, dest), value);
    },
    // True only when the row has a draft whose normalised value differs
    // from the saved quantity. Drives v-if on the green check so an
    // untouched row shows no save affordance; the check appears the
    // moment the typed number diverges from what's in the cart.
    qtyChanged(part, dest) {
      var k = this._qtyKey(part, dest);
      if (!Object.prototype.hasOwnProperty.call(this.qtyDrafts, k)) return false;
      var draftQty = Math.max(1, Math.floor(Number(this.qtyDrafts[k]) || 1));
      var committed = Math.max(1, Math.floor(Number(part.quantity) || 1));
      return draftQty !== committed;
    },
    commitQty(part, dest) {
      var clean = dest === "quote" ? "quote" : "order";
      var k = this._qtyKey(part, clean);
      var raw = Object.prototype.hasOwnProperty.call(this.qtyDrafts, k)
        ? this.qtyDrafts[k]
        : part.quantity;
      var qty = Math.max(1, Math.floor(Number(raw) || 1));
      if (this.standalone) {
        var arr = clean === "quote" ? this.internalQuoteParts : this.internalParts;
        var idx = arr.findIndex(function (p) {
          return p.partId === part.partId && p.poNumber === part.poNumber;
        });
        if (idx !== -1) {
          this.$set(arr, idx, Object.assign({}, arr[idx], { quantity: qty }));
        }
        this._commitQtyStandalone(part, clean, qty);
      } else {
        this.$emit("qty-change", {
          part: part,
          quantity: qty,
          destination: clean,
        });
      }
      // Drop the draft so the input now mirrors the committed value.
      this.$delete(this.qtyDrafts, k);
    },
    onContinueOrder() {
      if (this.standalone) {
        if (this.effectiveParts.length === 0) return;
        this.$router.push({ name: "Cart" });
      } else {
        this.$emit("continue-order");
      }
    },
    onContinueQuote() {
      if (this.standalone) {
        if (this.effectiveQuoteParts.length === 0) return;
        this.$router.push("/cart/rfqsaveditems");
      } else {
        this.$emit("continue-quote");
      }
    },

    // ===== STANDALONE-MODE INTERNALS =====
    // Verbatim shape-port of Account/Index.vue's syncFromActiveCart
    // and syncFromActiveQuoteCart. Reading directly from
    // apiService.getCart so the widget can populate itself without
    // any host-page involvement.
    async syncStandaloneFromActiveCart() {
      if (!localStorage.companyId) return;
      this._ensureWearPartIds();

      if (!localStorage.cartId || String(localStorage.cartId) === "0") {
        if (this.internalParts.length > 0) {
          this.internalParts = [];
        }
        return;
      }

      try {
        var cart = await apiService.getCart({ id: localStorage.cartId });
        if (!cart) return;

        var isQuote =
          cart.discriminator === "Rfq" ||
          cart.Discriminator === "Rfq" ||
          cart.discriminator === 1 ||
          cart.Discriminator === 1;
        if (isQuote) return;

        var pos = cart.purchaseOrders || [];
        if (pos.length === 0) {
          this.internalParts = [];
          return;
        }

        var derived = [];
        pos.forEach(function (po) {
          (po.parts || []).forEach(function (p) {
            var partInfo = p.part || {};
            derived.push({
              // PurchaseOrder row id — needed by the surgical
              // updateCartPartQuantity endpoint so a qty edit targets
              // the exact PO line (poNumber alone can collide across
              // assemblies/machines sharing a number).
              poId: po.id,
              partId: p.id,
              // Catalog part id (p.id is the PO-line id) — used for the
              // wear-discount lookup, which is keyed by catalog part id.
              catalogPartId: partInfo.id || partInfo.partId || partInfo.Id || 0,
              partCode: partInfo.code || "",
              description: partInfo.description || "",
              // Unit price for the line + the order total in the button.
              price: Number(partInfo.price || partInfo.Price || 0),
              poNumber: po.number || "",
              quantity: p.quantity || 1,
            });
          });
        });

        this.internalParts = derived;
      } catch (e) {
        console.warn("[CartWidget] standalone order-cart sync failed:", e);
      }
    },

    async syncStandaloneFromActiveQuoteCart() {
      if (!localStorage.companyId) return;

      if (!localStorage.cartIdRFQ || String(localStorage.cartIdRFQ) === "0") {
        if (this.internalQuoteParts.length > 0) {
          this.internalQuoteParts = [];
        }
        return;
      }

      try {
        var cart = await apiService.getCart({ id: localStorage.cartIdRFQ });
        if (!cart) return;

        var isQuote =
          cart.discriminator === "Rfq" ||
          cart.Discriminator === "Rfq" ||
          cart.discriminator === 1 ||
          cart.Discriminator === 1;
        if (!isQuote) return;

        var pos = cart.purchaseOrders || [];
        if (pos.length === 0) {
          this.internalQuoteParts = [];
          return;
        }

        var derived = [];
        pos.forEach(function (po) {
          (po.parts || []).forEach(function (p) {
            var partInfo = p.part || {};
            derived.push({
              // PurchaseOrder row id — needed by the surgical
              // updateCartPartQuantity endpoint so a qty edit targets
              // the exact PO line (poNumber alone can collide across
              // assemblies/machines sharing a number).
              poId: po.id,
              partId: p.id,
              // Catalog part id (p.id is the PO-line id) — used for the
              // wear-discount lookup, which is keyed by catalog part id.
              catalogPartId: partInfo.id || partInfo.partId || partInfo.Id || 0,
              partCode: partInfo.code || "",
              description: partInfo.description || "",
              // Unit price for the line + the quote total in the button (parity with orders).
              price: Number(partInfo.price || partInfo.Price || 0),
              poNumber: po.number || "",
              quantity: p.quantity || 1,
            });
          });
        });

        this.internalQuoteParts = derived;
      } catch (e) {
        console.warn("[CartWidget] standalone quote-cart sync failed:", e);
      }
    },

    // ===== Pricing + wear-discount helpers =====
    fmtPrice(n) {
      var v = Number(n) || 0;
      return "$" + v.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    // True when a cart line's catalog part is in the company's wear
    // catalog → show the "2% Discount" chip. Standalone lines carry the
    // catalog id as catalogPartId (their partId is the PO-line id);
    // controlled-mode lines fall back to partId / id (already the
    // catalog id on host selectedParts).
    isWearEligible(p) {
      if (!p) return false;
      var pid = p.catalogPartId || p.partId || p.id;
      return !!(pid != null && this.wearPartIds && this.wearPartIds[pid]);
    },
    // This company's wear part ids (its dbo.Wears = the Account Setup wear
    // list). The "2% Discount" chip must reflect the customer's OWN wear
    // parts, not the global wear-eligible catalog — so use the company-scoped
    // endpoint. Failure → empty map (chip just won't render).
    loadWearPartIds() {
      if (!apiService.getCompanyWearPartIds) return;
      var companyId = localStorage.companyId || "";
      this._wearCompanyId = companyId;
      if (!companyId) { this.wearPartIds = {}; return; }
      var self = this;
      apiService
        .getCompanyWearPartIds(companyId)
        .then(function (data) {
          var map = {};
          if (Array.isArray(data)) {
            data.forEach(function (id) { if (id != null) map[id] = true; });
          }
          self.wearPartIds = map;
        })
        .catch(function () { self.wearPartIds = {}; });
    },
    // Reload the wear map only when the selected company has changed
    // since the last load (called from the cart sync, which runs on
    // every cart-bus event).
    _ensureWearPartIds() {
      if (this._wearCompanyId !== (localStorage.companyId || "")) {
        this.loadWearPartIds();
      }
    },
    // Per-part remove (the × button) in standalone mode. Deletes the one
    // PurchaseOrderPart row via the surgical /cart/removePart endpoint. We
    // deliberately do NOT route this through _scheduleStandaloneSync (full
    // saveCart): an existing-cart save hits SaveOldCart, whose
    // DoesPartExistInTheCart guard rejects (400) the moment ANY incoming PO
    // already lives on the cart — which is ALWAYS true for the parts that
    // REMAIN after a removal, so the removal silently fails and a refresh
    // restores the unchanged server cart. Deleting the row by
    // (cartId, poId, partId) sidesteps the guard entirely (same rationale as
    // _commitQtyStandalone's surgical quantity update).
    _removeStandalone(part, destination) {
      if (!part) return;
      var dest = destination === "quote" ? "quote" : "order";
      var cartId = dest === "quote"
        ? Number(localStorage.cartIdRFQ || 0)
        : Number(localStorage.cartId || 0);
      if (!cartId || !part.poId || !part.partId) return;

      // Optimistically drop the row from the rendered queue.
      if (dest === "quote") {
        this.internalQuoteParts = this.internalQuoteParts.filter(function (p) {
          return !(p.partId === part.partId && p.poNumber === part.poNumber);
        });
      } else {
        this.internalParts = this.internalParts.filter(function (p) {
          return !(p.partId === part.partId && p.poNumber === part.poNumber);
        });
      }

      var self = this;
      apiService
        .removePart({
          cartId: cartId,
          purchaseOrderId: part.poId,
          partId: part.partId,
        })
        .then(function () {
          // Refresh header badges + any sibling widget instances.
          if (Vue.prototype.$cartBus) {
            Vue.prototype.$cartBus.$emit("cart-updated");
          }
        })
        .catch(function (e) {
          console.warn("[CartWidget] standalone remove failed:", e);
          // Roll the optimistic removal back to server truth.
          if (dest === "quote") {
            self.syncStandaloneFromActiveQuoteCart();
          } else {
            self.syncStandaloneFromActiveCart();
          }
          self.$bvToast.toast("That item couldn't be removed. Please try again.", {
            title: "Remove failed",
            variant: "danger",
            solid: true,
          });
        });
    },

    // Clear all (per destination) in standalone mode. Deletes the whole cart
    // for that destination via the EXISTING /cart/{id} DELETE endpoint — no
    // backend change needed. Like _removeStandalone, this avoids saveCart's
    // SaveOldCart guard. On success the matching localStorage pointer is
    // cleared so the now-deleted cart isn't re-fetched.
    _clearStandalone(destination) {
      if (!destination) {
        // Clear both sides.
        this._clearStandalone("order");
        this._clearStandalone("quote");
        return;
      }
      var dest = destination === "quote" ? "quote" : "order";
      var cartId = dest === "quote"
        ? Number(localStorage.cartIdRFQ || 0)
        : Number(localStorage.cartId || 0);
      // No cart for this destination — nothing to clear.
      if (!cartId) {
        if (dest === "quote") {
          this.internalQuoteParts = [];
        } else {
          this.internalParts = [];
        }
        return;
      }

      var self = this;
      apiService
        .deleteCart({ id: cartId })
        .then(function () {
          if (dest === "quote") {
            self.internalQuoteParts = [];
            try { localStorage.removeItem("cartIdRFQ"); } catch (e) { /* no-op */ }
          } else {
            self.internalParts = [];
            try { localStorage.removeItem("cartId"); } catch (e) { /* no-op */ }
          }
          if (Vue.prototype.$cartBus) {
            Vue.prototype.$cartBus.$emit("cart-updated");
          }
        })
        .catch(function (e) {
          console.warn("[CartWidget] standalone clear failed:", e);
          // Re-sync from the server so the tray reflects truth again.
          if (dest === "quote") {
            self.syncStandaloneFromActiveQuoteCart();
          } else {
            self.syncStandaloneFromActiveCart();
          }
          self.$bvToast.toast("The cart couldn't be cleared. Please try again.", {
            title: "Clear failed",
            variant: "danger",
            solid: true,
          });
        });
    },

    _removeInternal(part, destination) {
      if (!part) return;
      var dest = destination === "quote" ? "quote" : "order";
      if (dest === "quote") {
        this.internalQuoteParts = this.internalQuoteParts.filter(function (p) {
          return !(p.partId === part.partId && p.poNumber === part.poNumber);
        });
      } else {
        this.internalParts = this.internalParts.filter(function (p) {
          return !(p.partId === part.partId && p.poNumber === part.poNumber);
        });
      }
      this._scheduleStandaloneSync(dest);
    },

    _clearInternal(destination) {
      if (!destination) {
        this.internalParts = [];
        this.internalQuoteParts = [];
        this._scheduleStandaloneSync("order");
        this._scheduleStandaloneSync("quote");
        return;
      }
      var dest = destination === "quote" ? "quote" : "order";
      if (dest === "quote") {
        this.internalQuoteParts = [];
      } else {
        this.internalParts = [];
      }
      this._scheduleStandaloneSync(dest);
    },

    // Persist a single standalone quantity edit via the surgical
    // /cart/updatePartQuantity endpoint. We deliberately do NOT route
    // qty edits through _scheduleStandaloneSync (full saveCart): an
    // existing-cart save hits SaveOldCart, whose DoesPartExistInTheCart
    // guard rejects (400) any PO whose part is already on the cart —
    // always true when you're just changing the quantity of a part
    // that's already there. Patching the one PurchaseOrderPart row by
    // (cartId, poId, partId) avoids the guard entirely.
    _commitQtyStandalone(part, destination, qty) {
      var dest = destination === "quote" ? "quote" : "order";
      var cartId = dest === "quote"
        ? Number(localStorage.cartIdRFQ || 0)
        : Number(localStorage.cartId || 0);
      if (!cartId || !part || !part.poId || !part.partId) return;

      var self = this;
      apiService
        .updateCartPartQuantity({
          cartId: cartId,
          purchaseOrderId: part.poId,
          partId: part.partId,
          quantity: qty,
        })
        .then(function () {
          // Refresh header badges + any sibling widget instances.
          if (Vue.prototype.$cartBus) {
            Vue.prototype.$cartBus.$emit("cart-updated");
          }
        })
        .catch(function (e) {
          console.warn("[CartWidget] standalone qty save failed:", e);
          // Roll the optimistic local bump back to server truth.
          if (dest === "quote") {
            self.syncStandaloneFromActiveQuoteCart();
          } else {
            self.syncStandaloneFromActiveCart();
          }
        });
    },

    // Lightweight server-sync for standalone mutations. Unlike the
    // host pages' full debounced sync (which also handles
    // create-new-cart paths from the picker modal), the widget in
    // standalone mode is only ever editing an EXISTING cart — by
    // definition, since it can only show parts that are already in
    // a cart. So a direct saveCart suffices.
    //
    // Mirrors the payload shape Account/Index.vue's
    // syncQueueToCart produces.
    _scheduleStandaloneSync(destination) {
      var dest = destination === "quote" ? "quote" : "order";
      var cartId = dest === "quote"
        ? Number(localStorage.cartIdRFQ || 0)
        : Number(localStorage.cartId || 0);
      if (!cartId) return;

      var arr = dest === "quote" ? this.internalQuoteParts : this.internalParts;

      // Group parts by poNumber for the API shape.
      var groups = {};
      var order = [];
      arr.forEach(function (p) {
        var po = p.poNumber || "";
        if (!groups[po]) {
          groups[po] = { number: po, parts: [] };
          order.push(po);
        }
        groups[po].parts.push({
          id: p.partId,
          quantity: p.quantity || 1,
        });
      });
      var purchaseOrders = order.map(function (k) { return groups[k]; });

      var payload = {
        id: cartId,
        companyId: Number(localStorage.companyId || 0),
        discriminator: dest === "quote" ? "Rfq" : "Order",
        purchaseOrders: purchaseOrders,
      };

      apiService.saveCart(payload).then(() => {
        // Tell the rest of the app so the header badges + any
        // other CartWidget instances refresh. Same event other
        // mutators emit.
        if (Vue.prototype.$cartBus) {
          Vue.prototype.$cartBus.$emit("cart-updated");
        }
      }).catch((e) => {
        console.warn("[CartWidget] standalone save failed:", e);
      });
    },
  },
};
</script>

<style scoped>
/* =============================================================
   STAGING TRAY — moved here verbatim from Account/Index.vue's
   <style> block. Keeping the class names so the existing markup
   shape is preserved and the design reads identically to the
   source page screenshot the user approved.
   ============================================================= */
.cart-widget-host {
  /* Pass-through container; layout-effecting styles live on
     .staging-tray-wrapper itself so the widget can drop into any
     grid cell. */
}

/* In the floating stack, when the expanded panel isn't showing
   (collapsed to the side pill, or the cart is empty) the host must take
   NO vertical space — otherwise it stays a 0-content flex item and the
   stack's row-gap pushes the Top 5 panel below it downward. Dropping the
   host with display:none lets Top 5 become the first flex item so it
   rises to align with the content top, exactly as the cart does when it
   IS shown. The collapsed pill is portaled to App.vue's root, so hiding
   the host never hides the pill. */
.cart-widget-host.cart-host-collapsed {
  display: none;
}

/* Floating mode (global right-edge stack in App.vue). The stack container
   (now position:ABSOLUTE) scrolls WITH the page, so the cart tray renders at
   its NATURAL height and the user scrolls the PAGE to see a long cart — no
   internal viewport-height cap / inner scrollbar. (Previously the stack was
   position:fixed and this capped the tray to min(70vh,620px) with overflow-y:
   auto so a long cart scrolled inside its own panel; that's no longer wanted
   now that the whole stack scrolls with the document.) The Top 5 panel below
   simply flows further down the page. Collapsed-pill styles are untouched. */
.cart-widget-floating .staging-tray-wrapper {
  width: 100%;
}

.staging-tray-wrapper {
  background: #fff;
  border: 1px solid #ececec;
  border-top: 3px solid #c0392b;
  border-radius: 6px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  padding: 14px 14px 12px;
  display: flex;
  flex-direction: column;
}
.staging-tray-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}
.staging-tray-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #c0392b;
}
.staging-tray-title {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #c0392b;
  /* Push the collapse button to the far right of the header. */
  margin-right: auto;
}
.cart-collapse-btn {
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: 50%;
  background: #f0f0f0;
  color: #555;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, color 0.15s ease;
}
.cart-collapse-btn:hover {
  background: #c0392b;
  color: #fff;
}
.cart-collapse-btn:focus-visible {
  outline: 2px solid #c0392b;
  outline-offset: 2px;
}
.staging-tray-body {
  flex: 1;
}
.staging-continue-btn {
  width: 100%;
  margin-top: 10px;
  padding: 9px 12px;
  background: #c0392b;
  color: #fff;
  border: 0;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  cursor: pointer;
  transition: background 0.15s ease;
}
.staging-continue-btn:hover {
  background: #a3301f;
}
.staging-continue-btn-quote {
  background: #444;
}
.staging-continue-btn-quote:hover {
  background: #222;
}

/* =============================================================
   INSIGHTS QUEUE ROWS — same as Account/Index.vue.
   ============================================================= */
.insights-selected-tray {
  margin: 0;
}
.insights-selected-title {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  margin-bottom: 4px;
}
.insights-selected-title-text {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #444;
}
.insights-selected-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.insights-selected-label {
  font-size: 11px;
  color: #888;
}
.insights-selected-clear {
  font-size: 11px;
  color: #c0392b;
  background: none;
  border: 0;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
}
.insights-selected-clear:hover {
  color: #8e2317;
}
.insights-order-group {
  background: #fff;
  border-radius: 4px;
  padding: 6px 8px;
  margin-bottom: 6px;
}
.insights-order-group:first-of-type {
  /* tighter top spacing — header above already provides it */
}
.insights-order-group-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 4px;
}
.insights-order-group-num {
  /* White chip with a red border and black text — a high-contrast PO# /
     Quote Reference# badge that reads clearly in light, mid, and dark modes. */
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  color: #000000;
  background: #ffffff;
  border: 1px solid #c0392b;
  border-radius: 10px;
  padding: 1px 8px;
  letter-spacing: 0.02em;
  white-space: nowrap;
}
.insights-order-group-meta {
  font-size: 10px;
  color: #888;
}
.insights-queue-rows {
  list-style: none;
  margin: 0;
  padding: 0;
}
.insights-queue-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
  padding: 5px 0;
  border-bottom: 1px solid #ececec;
  font-size: 11px;
}
.insights-queue-row:last-child {
  border-bottom: 0;
}
.insights-queue-row-price {
  flex: 0 0 auto;
  font-weight: 700;
  color: #c0392b;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
/* Push the qty group (and the remove × after it) to the right edge,
   leaving code · chip · price on the left of the single row. */
.insights-queue-row .insights-queue-row-qty {
  margin-left: auto;
}
.insights-queue-discount {
  flex: 0 0 auto;
  font-size: 8.5px;
  font-weight: 700;
  color: #1f7a3a;
  background: #e9f7ef;
  border: 1px solid #bfe6cd;
  border-radius: 10px;
  padding: 1px 5px;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.insights-queue-row-code {
  flex: 0 1 auto;
  min-width: 0;
  font-weight: 600;
  color: #222;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.insights-queue-row-qty {
  display: flex;
  align-items: center;
  gap: 3px;
}
.insights-qty-input {
  width: 40px;
  padding: 2px 3px;
  font-size: 11px;
  border: 1px solid #d6d6d6;
  border-radius: 3px;
  text-align: center;
  background: #fff;
  color: #222;
}
.insights-qty-input:focus {
  outline: none;
  border-color: #c0392b;
  box-shadow: 0 0 0 2px rgba(192, 57, 43, 0.12);
}
.insights-qty-input::-webkit-inner-spin-button,
.insights-qty-input::-webkit-outer-spin-button {
  opacity: 1;
}
.insights-qty-label {
  font-size: 10px;
  color: #888;
}
/* Green check — commits the edited quantity (sits right of the field). */
.insights-qty-save {
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: 50%;
  background: #2e9e4f;
  color: #fff;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s ease;
}
.insights-qty-save:hover {
  background: #25813f;
}
.insights-qty-save:focus-visible {
  outline: 2px solid #2e9e4f;
  outline-offset: 2px;
}
.insights-queue-row-remove {
  width: 20px;
  height: 20px;
  /* Breathing room between the "qty" label and the remove ×. */
  margin-left: 8px;
  border: 0;
  border-radius: 50%;
  background: #f0f0f0;
  color: #888;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background 0.15s ease, color 0.15s ease;
}
.insights-queue-row-remove:hover {
  background: #c0392b;
  color: #fff;
}

.insights-selected-title-quote {
  margin-top: 14px;
  padding-top: 10px;
  border-top: 1px solid #e6e6e6;
}
.insights-quote-group .insights-order-group-num {
  color: #555;
}
</style>

<style>
/* =============================================================
   COLLAPSED PILL — UNSCOPED.
   The pill renders through <portal to="cart-pill"> into App.vue's
   root, so it leaves this component's scope. Scoped CSS wouldn't
   apply. Class names are namespaced (.cart-pill*) so unscoped
   rules are still safe.

   Positioning: fixed to the right viewport edge, vertically
   centered. Z-index is high enough to clear the side rails
   (z: 999) and any modal-adjacent overlays under 9000. The
   portal-target lives at #app's root so this element is a child
   of #app, not nested in any stickyHeader stacking context — the
   z-index actually counts in the root context.
   ============================================================= */
.cart-pill {
  /* Positioning is owned by the .floating-pill-rail container in App.vue
     (a fixed flex column the pill is portaled into). The pill itself is a
     static flex child — no fixed/top/transform here, so the rail's gap
     governs the spacing between it and the Top 5 / Info pills. */
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;

  background: #fff;
  color: #c0392b;
  border: 1px solid #ececec;
  border-left: 3px solid #c0392b;
  border-radius: 0 8px 8px 0;
  /* Square LEFT corners so it butts flush against the steel rail's inner edge
     like a tab/pull-tab growing out of the content; rounded on the free right
     side. (Mirrored 2026-08-02 — was square-right / rounded-left.) */
  box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.15);
  padding: 10px 8px;
  /* Fixed width so the cart pill and the Top 5 pill are the same
     size when stacked on the right edge. */
  min-width: 56px;
  width: 56px;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s ease, transform 0.18s ease;
}
.cart-pill:hover {
  background: #fafafa;
  transform: translateX(-2px);
}
.cart-pill:focus-visible {
  outline: 2px solid #c0392b;
  outline-offset: 2px;
}
.cart-pill-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}
.cart-pill-counts {
  display: flex;
  flex-direction: column;
  gap: 3px;
  align-items: center;
}
.cart-pill-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 11px;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  color: #fff;
}
.cart-pill-badge-orders {
  background: #c0392b;
}
.cart-pill-badge-quotes {
  background: #444;
}
</style>